import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Label, Select, SelectTrigger, Textarea } from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import PropTypes from "prop-types";
import { CodeBlockDialogValidator } from "~/components/TinyMCEDialogs/formValidators";

export const languageCodeBlockList = [
  { text: "Plain Text", value: "plain" },
  { text: "Markup (HTML, XML…)", value: "markup" },
  { text: "C/C++", value: "clike" },
  { text: "JavaScript", value: "js" },
  { text: "JSON", value: "json" },
  { text: "TypeScript", value: "ts" },
  { text: "React", value: "jsx" },
  { text: "SQL", value: "sql" },
  { text: "Python", value: "py" },
  { text: "Go", value: "go" },
  { text: "Ruby", value: "rb" },
  { text: "Rust", value: "rust" },
  { text: "Elixir", value: "elixir" },
  { text: "PHP", value: "php" },
  { text: "Perl", value: "perl" },
  { text: "Java", value: "java" },
  { text: "Kotlin", value: "kotlin" },
  { text: "YAML", value: "yaml" },
  { text: ".NET (C#)", value: "dotnet" },
  { text: "Bash", value: "shell" },
  { text: "Gherkin", value: "gherkin" },
  { text: "Regex", value: "regex" },
  { text: "Swift", value: "swift" },
  { text: "Objective-C", value: "objc" },
  { text: "nginx", value: "nginx" },
  { text: "Apache Configuration", value: "apacheconf" },
  { text: "Docker", value: "docker" },
  { text: "Ini", value: "ini" },
  { text: ".ignore", value: "ignore" },
];

export const CodeBlockDialog = ({ onClose, onSubmit, selectedLanguage, code, ...rest }) => {
  const { t } = useTranslation();

  const options = useMemo(() => {
    return languageCodeBlockList.map((lang) => ({
      name: lang.text,
      id: lang.value,
    }));
  }, []);

  const handleSubmit = useCallback(
    (value) => {
      onSubmit?.(value.language, value.code);
      onClose?.();
    },
    [onSubmit, onClose]
  );

  return (
    <Dialog
      {...rest}
      onClose={onClose}
    >
      <Formik
        initialValues={{ language: selectedLanguage || options[0].id, code }}
        onSubmit={handleSubmit}
        validationSchema={CodeBlockDialogValidator}
        validateOnMount
      >
        {() => (
          <Form role="form">
            <Dialog.Title>{t("Insert Code Block")}</Dialog.Title>
            <Dialog.ContentDivider />
            <Dialog.Content>
              <div className="tw-mb-4">
                <Field name="language">
                  {({ field, form, meta }) => (
                    <>
                      <Label
                        size="small"
                        htmlFor="language"
                        invalid={meta.touched && meta.error}
                        className="tw-mb-1"
                      >
                        {t("Language")}
                      </Label>
                      <Select
                        selected={field.value}
                        options={options}
                        type="single"
                        invalid={meta.touched && meta.error}
                        onChange={(value) => {
                          form.setFieldValue(field.name, value);
                        }}
                        target={
                          <SelectTrigger className="tw-w-56">
                            {options.find((lang) => lang.id === field.value)?.name}
                          </SelectTrigger>
                        }
                        forceCloseMenu
                      />
                    </>
                  )}
                </Field>
              </div>
              <div>
                <Field name="code">
                  {({ field, form, meta }) => (
                    <>
                      <Label
                        size="small"
                        htmlFor="code"
                        invalid={meta.touched && meta.error}
                        className="tw-mb-1"
                      >
                        {t("Code")}
                      </Label>
                      <Textarea
                        value={field.value}
                        rows={8}
                        invalid={meta.touched && meta.error}
                        className="tw-w-full tw-font-mono"
                        onChange={(event) => {
                          form.setFieldValue(field.name, event.target.value);
                        }}
                      />
                    </>
                  )}
                </Field>
              </div>
            </Dialog.Content>
            <Dialog.ContentDivider />
            <Dialog.Actions>
              <Button
                type="submit"
                className="tw-mr-3"
              >
                {t("Save")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
              >
                {t("Cancel")}
              </Button>
            </Dialog.Actions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

CodeBlockDialog.propTypes = {
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  code: PropTypes.string,
  selectedLanguage: PropTypes.string,
};
CodeBlockDialog.defaultProps = {
  selectedLanguage: "plain",
};
